// Pure model for the LO Ads Dashboard page (ALA-577). No React / DOM here — the
// render layer (Ads.tsx / AdsSection.tsx) consumes these. The aggregate
// (ALA-575; M2 ads level per Dev #3325) is spend-free by construction; per the
// revised design (AlaudaAI/contexts #18) it also carries no conversions (D7).
// Preview data exists ONLY as the two per-ad fields (D4) — the campaign object
// never carries one. This model carries ONLY the approved fields, so no banned
// value can reach the UI; a malformed ads level degrades the ads SECTION, never
// the campaign summary (D9/G10).

export interface AdsCampaign {
  id: string;
  name: string;
  platform: string;
  status: string;
}

export interface AdsKpis {
  impressions: number;
  clicks: number;
  ctr: number | null;
}

export interface AdsTrendPoint {
  date: string;
  impressions: number;
  clicks: number;
}

export interface AdsFreshness {
  status: "ok" | "stale" | "failed" | "never";
  lastSyncedAt: string | null;
}

/**
 * One currently-running ad (M2 ads level, gap G10). The Go proxy (#3325)
 * serializes the eight required fields; rows are informational (D9) — they
 * carry no additivity promise against the campaign KPIs. ctr is proxy-derived:
 * null when impressions == 0, otherwise clamped to at most 1. name and the two
 * preview fields are nullable by upstream contract (D4).
 *
 * copy / imageUrl are the ads-manager per-ad creative fields (see handoff
 * api-ads-contract.md). Both are OPTIONAL on the wire so this page deploys
 * safely before or after the backend grows them: a missing key and an
 * explicit null behave identically. imageUrl safety (https-only) is enforced
 * at render time with the same scheme check previews use.
 */
export interface AdsAdRow {
  id: string;
  name: string | null;
  /** Exactly "active": the contract serializes only running ads (D9). */
  status: "active";
  impressions: number;
  clicks: number;
  ctr: number | null;
  previewUrl: string | null;
  previewExpiresAt: string | null;
  copy?: string | null;
  imageUrl?: string | null;
}

export interface AdsDashboard {
  campaign: AdsCampaign;
  window: { from: string; to: string };
  kpis: AdsKpis;
  trend: AdsTrendPoint[];
  freshness: AdsFreshness;
  /**
   * The M2 ads level (G10), tri-state per the #3325 proxy: rows in upstream
   * order, [] when upstream confirmed zero running ads, null when the per-ad
   * read degraded. A body from BEFORE #3325 has no key at all — treated
   * exactly like null. This field arrives raw; interpretAdsSection() is the
   * only reader.
   */
  ads?: AdsAdRow[] | null;
}

export type AdsViewState =
  | { kind: "loading" }
  | { kind: "ready"; dashboard: AdsDashboard }
  | { kind: "notEnrolled" }
  | { kind: "unavailable" };

export type AdsEnrollmentState = "checking" | "enrolled" | "notEnrolled" | "unauthorized" | "unavailable";

/**
 * HomePage navigation state for the shared dashboard request. Only an explicit
 * 404 proves that this LO is not enrolled. A 401 is kept distinct from
 * enrollment, while network/upstream failures retain an Ads entry so direct
 * navigation can explain the temporary outage.
 */
export function interpretAdsEnrollmentStatus(status: number): AdsEnrollmentState {
  if (status === 200) return "enrolled";
  if (status === 404) return "notEnrolled";
  if (status === 401) return "unauthorized";
  return "unavailable";
}

export function shouldShowAdsNav(state: AdsEnrollmentState): boolean {
  return state === "enrolled" || state === "unavailable";
}

/**
 * Map the HTTP status (+ parsed body) from GET /los/ads/dashboard to a view
 * state. 404 → not enrolled (a friendly state, not an error); 200 with a body
 * that satisfies the full dashboard contract → ready; everything else (a
 * malformed 200, 401, 5xx, network) → temporarily unavailable.
 */
export function interpretAdsResponse(status: number, body: unknown): AdsViewState {
  if (status === 404) {
    return { kind: "notEnrolled" };
  }
  if (status === 200 && isValidDashboard(body)) {
    return { kind: "ready", dashboard: body };
  }
  return { kind: "unavailable" };
}

/**
 * Runtime validation of the full T4 aggregate contract. A 200 whose body is
 * missing campaign/window/kpis/trend/freshness (or has the wrong shapes) is
 * treated as unavailable rather than rendered into a crashing "ready" state.
 */
export function isValidDashboard(body: unknown): body is AdsDashboard {
  if (body === null || typeof body !== "object") return false;
  const b = body as Record<string, unknown>;

  // The banned-token scan deliberately EXCLUDES the ads rows: previewUrl /
  // previewExpiresAt are legitimate exactly there (design D4, mirroring the Go
  // proxy's rescope in #3325), and a malformed or banned-key ad row degrades
  // the SECTION via interpretAdsSection — never the whole page (D9/G10).
  // Everything else — campaign, kpis, trend, freshness, top level — keeps the
  // full ban, so the campaign object still can't smuggle a preview key.
  const outsideAds: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(b)) {
    if (key !== "ads") outsideAds[key] = value;
  }
  if (containsSpendShapedKey(outsideAds)) return false;

  const c = b.campaign as Record<string, unknown> | undefined;
  if (
    !c ||
    !isNonEmptyString(c.id) ||
    !isNonEmptyString(c.name) ||
    !isNonEmptyString(c.platform) ||
    !isNonEmptyString(c.status)
  ) {
    return false;
  }

  const w = b.window as Record<string, unknown> | undefined;
  if (!w || !isThirtyDayWindow(w.from, w.to)) return false;

  const k = b.kpis as Record<string, unknown> | undefined;
  if (
    !k ||
    !isNonNegativeInteger(k.impressions) ||
    !isNonNegativeInteger(k.clicks) ||
    !isNullableRate(k.ctr)
  ) {
    return false;
  }

  if (!Array.isArray(b.trend) || !b.trend.every(isValidTrendPoint)) return false;

  const f = b.freshness as Record<string, unknown> | undefined;
  if (!f || !isValidFreshness(f)) return false;

  return true;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function isThirtyDayWindow(from: unknown, to: unknown): boolean {
  const fromTime = parseISODate(from);
  const toTime = parseISODate(to);
  return fromTime !== null && toTime !== null && toTime - fromTime === 29 * 24 * 60 * 60 * 1000;
}

function parseISODate(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const time = Date.UTC(year, month - 1, day);
  const parsed = new Date(time);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return time;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function isNonNegativeFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isNullableRate(value: unknown): value is number | null {
  return value === null || (isNonNegativeFiniteNumber(value) && value <= 1);
}

function isValidTrendPoint(value: unknown): value is AdsTrendPoint {
  if (value === null || typeof value !== "object") return false;
  const point = value as Record<string, unknown>;
  return (
    parseISODate(point.date) !== null &&
    isNonNegativeInteger(point.impressions) &&
    isNonNegativeInteger(point.clicks)
  );
}

function isValidFreshness(value: Record<string, unknown>): value is Record<string, unknown> & AdsFreshness {
  const status = value.status;
  if (status !== "ok" && status !== "stale" && status !== "failed" && status !== "never") {
    return false;
  }

  const stamp = value.lastSyncedAt;
  if (status === "never") return stamp === null;
  if (status === "ok") return isRFC3339Timestamp(stamp);
  return stamp === null || isRFC3339Timestamp(stamp);
}

function containsSpendShapedKey(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(containsSpendShapedKey);
  if (value === null || typeof value !== "object") return false;
  return Object.entries(value as Record<string, unknown>).some(
    ([key, nested]) => isSpendShapedKey(key) || containsSpendShapedKey(nested),
  );
}

// ---------------------------------------------------------------------------
// M2 ads level (gap G10, design rev AlaudaAI/contexts #18 D4/D9)
// ---------------------------------------------------------------------------

export type AdsSectionState =
  | { kind: "rows"; ads: AdsAdRow[] }
  | { kind: "empty" }
  | { kind: "unavailable" };

/**
 * Interpret the tri-state ads field. Rows render in delivered (upstream)
 * order; [] is the real "no ads currently running" state. Anything else —
 * null, a MISSING key (a pre-#3325 proxy body), a non-array, or ANY
 * malformed/banned-key row — degrades this SECTION to unavailable. The
 * campaign summary is never affected (D9: campaign totals stay authoritative;
 * ad rows are informational).
 */
export function interpretAdsSection(ads: unknown): AdsSectionState {
  if (!Array.isArray(ads)) return { kind: "unavailable" };
  if (ads.length === 0) return { kind: "empty" };
  if (!ads.every(isValidAdRow)) return { kind: "unavailable" };
  return { kind: "rows", ads };
}

/**
 * The ONLY spellings on which preview data is legitimate — exactly the two
 * ad-row fields the Go proxy serializes (D4). Any other preview-shaped key,
 * on a row or anywhere else, stays banned.
 */
const AD_ROW_PREVIEW_KEYS: readonly string[] = ["previewUrl", "previewExpiresAt"];

/**
 * Per-row validation at the strictness the campaign contract already uses:
 * required strings non-empty, counts non-negative integers, ctr null or a
 * finite rate <= 1 (the proxy clamps overdelivery), nullable fields valid
 * when present. The spend/conversion/money-family ban covers every row key
 * except the two legitimate preview fields; nested values keep the full ban.
 */
function isValidAdRow(value: unknown): value is AdsAdRow {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  const hasBannedKey = Object.entries(row).some(
    ([key, nested]) =>
      (isSpendShapedKey(key) && !AD_ROW_PREVIEW_KEYS.includes(key)) || containsSpendShapedKey(nested),
  );
  if (hasBannedKey) return false;
  return (
    isNonEmptyString(row.id) &&
    (row.name === null || isNonEmptyString(row.name)) &&
    // The api-ads contract pins status to exactly "active": only running ads
    // are ever serialized (Glen finish-line: "only running ads"). Any other
    // status is upstream misbehavior and FAILS CLOSED to the section note
    // rather than silently filtering rows.
    row.status === "active" &&
    isNonNegativeInteger(row.impressions) &&
    isNonNegativeInteger(row.clicks) &&
    isNullableRate(row.ctr) &&
    (row.previewUrl === null || isNonEmptyString(row.previewUrl)) &&
    // Expiry is TYPE-checked only (string | null). Semantic validity belongs
    // to derivePreviewState: the design's error-states table gives a past OR
    // unparseable expiry the per-ad "being refreshed" treatment, so a bad
    // timestamp must degrade that ad alone, never the whole section.
    (row.previewExpiresAt === null || typeof row.previewExpiresAt === "string") &&
    // Optional creative fields: missing == null (any-merge-order safety with
    // the backend rollout); anything present must be null or a real string.
    (row.copy === undefined || row.copy === null || isNonEmptyString(row.copy)) &&
    (row.imageUrl === undefined || row.imageUrl === null || isNonEmptyString(row.imageUrl))
  );
}

// ---------------------------------------------------------------------------
// Per-ad preview treatment (M2, design D3/D4). Resurrected from the M1-era
// safe-attrs approach with ONE deliberate change: the exact-host origin pin
// was removed (preview links aren't guaranteed to live on a single Google
// host); the https scheme check stays. FAIL CLOSED at every step.
// ---------------------------------------------------------------------------

export type AdsPreviewState =
  | { kind: "clickout"; url: string } // a safe link with a valid FUTURE expiry
  | { kind: "refreshing" } // expiry past, exactly now, unparseable, or null
  | { kind: "hidden" }; // previewUrl null or unsafe

/**
 * True only for a clean absolute https URL with no credentials and no
 * whitespace. A javascript:, data:, http:, protocol-relative, or malformed
 * URL is unsafe to place in an href and must never reach a click-out.
 */
export function isSafePreviewUrl(url: string | null | undefined): boolean {
  if (!url || /\s/.test(url)) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.username === "" && parsed.password === "";
  } catch {
    return false;
  }
}

/**
 * Decide one ad's "View this ad" treatment. FAIL CLOSED: a null or unsafe URL
 * hides the action entirely; ONLY a safe https URL with a valid FUTURE expiry
 * clicks out. Everything else about the expiry (past, exactly now,
 * unparseable, or null) shows "being refreshed" for that ad alone: per the
 * api-ads contract, an absent expiry gives no liveness guarantee and a
 * possibly-dead link must never render.
 */
export function derivePreviewState(
  previewUrl: string | null | undefined,
  previewExpiresAt: string | null | undefined,
  now: Date,
): AdsPreviewState {
  if (previewUrl == null || !isSafePreviewUrl(previewUrl)) {
    return { kind: "hidden" };
  }
  if (previewExpiresAt == null) {
    return { kind: "refreshing" };
  }
  // Only the canonical API timestamp contract is accepted. Browser-specific
  // date parsing must not turn an invalid expiry into a live click-out.
  if (!isRFC3339Timestamp(previewExpiresAt) || Date.parse(previewExpiresAt) <= now.getTime()) {
    return { kind: "refreshing" };
  }
  return { kind: "clickout", url: previewUrl };
}

function isRFC3339Timestamp(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) {
    return false;
  }
  return parseISODate(value.slice(0, 10)) !== null && Number.isFinite(Date.parse(value));
}

// ---------------------------------------------------------------------------
// Client-side date range (Google Ads style presets + custom calendar). This
// is PURE slicing of the 30-day daily series the single response already
// carries: no function here can influence the request shape (still the one
// JWT-only GET, pinned in ads-client.test.ts). Presets follow Google Ads
// calendar semantics ("Last N days" ends yesterday and excludes today);
// every selection is clamped into the served window, and an empty
// intersection is an honest zero state, never an error.
// ---------------------------------------------------------------------------

export type AdsRangePresetKey = "today" | "yesterday" | "last7" | "last14" | "last30";

/** An inclusive calendar-day range, both ends "YYYY-MM-DD". */
export interface AdsDateRange {
  from: string;
  to: string;
}

/** The preset list in display order. Exposed so a test can pin the set. */
export const ADS_RANGE_PRESETS: readonly { key: AdsRangePresetKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
  { key: "last14", label: "Last 14 days" },
  { key: "last30", label: "Last 30 days" },
];

const DAY_MS = 24 * 60 * 60 * 1000;

function isoFromTime(time: number): string {
  return new Date(time).toISOString().slice(0, 10);
}

/**
 * Resolve a preset against an anchor calendar day (the caller's "today").
 * All math is UTC on calendar days. Fails closed (null) on a bad anchor.
 */
export function presetDateRange(key: AdsRangePresetKey, todayISO: string): AdsDateRange | null {
  const today = parseISODate(todayISO);
  if (today === null) return null;
  switch (key) {
    case "today":
      return { from: isoFromTime(today), to: isoFromTime(today) };
    case "yesterday":
      return { from: isoFromTime(today - DAY_MS), to: isoFromTime(today - DAY_MS) };
    case "last7":
      return { from: isoFromTime(today - 7 * DAY_MS), to: isoFromTime(today - DAY_MS) };
    case "last14":
      return { from: isoFromTime(today - 14 * DAY_MS), to: isoFromTime(today - DAY_MS) };
    case "last30":
      return { from: isoFromTime(today - 30 * DAY_MS), to: isoFromTime(today - DAY_MS) };
    default:
      return null;
  }
}

/**
 * Intersect a requested range with the served data window. null means the
 * intersection is empty (or an input was malformed/inverted): the honest
 * zero state, distinct from any error state.
 */
export function clampRangeToWindow(
  range: AdsDateRange,
  window: { from: string; to: string },
): AdsDateRange | null {
  const from = parseISODate(range.from);
  const to = parseISODate(range.to);
  const windowFrom = parseISODate(window.from);
  const windowTo = parseISODate(window.to);
  if (from === null || to === null || windowFrom === null || windowTo === null) return null;
  const clampedFrom = Math.max(from, windowFrom);
  const clampedTo = Math.min(to, windowTo);
  if (clampedFrom > clampedTo) return null;
  return { from: isoFromTime(clampedFrom), to: isoFromTime(clampedTo) };
}

/** Keep the daily points inside the inclusive range, in delivered order. */
export function sliceTrend(trend: AdsTrendPoint[], range: AdsDateRange): AdsTrendPoint[] {
  const from = parseISODate(range.from);
  const to = parseISODate(range.to);
  if (from === null || to === null) return [];
  return trend.filter((point) => {
    const time = parseISODate(point.date);
    return time !== null && time >= from && time <= to;
  });
}

/**
 * Re-sum a slice into the three approved KPIs, deriving ctr with the proxy's
 * own rules: null when impressions == 0, clamped to at most 1.
 */
export function summarizeTrend(points: AdsTrendPoint[]): AdsKpis {
  let impressions = 0;
  let clicks = 0;
  for (const point of points) {
    impressions += point.impressions;
    clicks += point.clicks;
  }
  return { impressions, clicks, ctr: impressions > 0 ? Math.min(1, clicks / impressions) : null };
}

export interface AdsRangeView {
  /** The effective (clamped) range, or null when nothing falls in the window. */
  range: AdsDateRange | null;
  trend: AdsTrendPoint[];
  kpis: AdsKpis;
  isFullWindow: boolean;
}

/**
 * Everything the page needs for one selected range. When the effective range
 * covers the FULL served window the server kpis are returned verbatim (the
 * campaign totals stay authoritative per D9); any narrower range re-sums the
 * slice client-side.
 */
export function deriveRangeView(
  dashboard: Pick<AdsDashboard, "window" | "kpis" | "trend">,
  range: AdsDateRange | null,
): AdsRangeView {
  const clamped = range ? clampRangeToWindow(range, dashboard.window) : null;
  if (!clamped) {
    return {
      range: null,
      trend: [],
      kpis: { impressions: 0, clicks: 0, ctr: null },
      isFullWindow: false,
    };
  }
  const isFullWindow = clamped.from === dashboard.window.from && clamped.to === dashboard.window.to;
  const trend = sliceTrend(dashboard.trend, clamped);
  return {
    range: clamped,
    trend,
    kpis: isFullWindow ? dashboard.kpis : summarizeTrend(trend),
    isFullWindow,
  };
}

export interface AdsCopyParts {
  headline: string;
  body: string | null;
}

/**
 * Split ad copy per the api-ads contract's "headline\n\nbody" convention:
 * the first line is the headline, everything after is the body (null when
 * there is none). Both halves are trimmed.
 */
export function splitAdCopy(copy: string): AdsCopyParts {
  const [first, ...rest] = copy.split(/\r?\n/);
  const body = rest.join("\n").trim();
  return { headline: first.trim(), body: body === "" ? null : body };
}

export interface FreshnessLine {
  text: string;
  tone: "ok" | "warn" | "muted";
}

/** A short human line describing sync freshness. */
export function freshnessLine(f: AdsFreshness): FreshnessLine {
  switch (f.status) {
    case "ok":
      return { text: f.lastSyncedAt ? `Updated ${formatWhen(f.lastSyncedAt)}` : "Up to date", tone: "muted" };
    case "stale":
      return {
        text: f.lastSyncedAt ? `Last updated ${formatWhen(f.lastSyncedAt)}, may be out of date` : "May be out of date",
        tone: "warn",
      };
    case "failed":
      return { text: "The last update didn't complete; numbers below may be incomplete.", tone: "warn" };
    case "never":
      return { text: "No data has synced yet.", tone: "muted" };
    default:
      return { text: "", tone: "muted" };
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "recently" : d.toLocaleDateString();
}

/**
 * The KPI tiles the page shows — impressions, clicks, CTR ONLY (design rev
 * AlaudaAI/contexts #18 D7: no conversions). There is no spend tile by
 * construction. Exposed so a test can pin the set.
 */
export const ADS_KPI_KEYS = ["impressions", "clicks", "ctr"] as const;

/**
 * Banned field names that must never appear anywhere the page receives or
 * renders: spend-shaped (design D5) plus the conversion* and preview* families
 * and the remaining money tokens the Go proxy bans (design rev AlaudaAI/contexts
 * #18 D7: budget, amount, currency, micros, cpa). Used by the leak-guard test.
 * ONE scoped exception, mirroring the Go side (#3325): the exact ad-row fields
 * previewUrl / previewExpiresAt are legitimate (D4) — see AD_ROW_PREVIEW_KEYS
 * in isValidAdRow. Every other surface, including the campaign object, keeps
 * the full preview ban.
 */
export const SPEND_SHAPED_KEYS = [
  "spend",
  "cpc",
  "cpm",
  "cpl",
  "cost",
  "costperlead",
  "cpa",
  "roi",
  "roas",
  "conversion",
  "conversions",
  "budget",
  "amount",
  "currency",
  "micros",
  "keyword",
  "keywords",
  "targeting",
];

/**
 * The banned FAMILY words (money families per design D5/D7, plus keyword/
 * targeting per the same hard rule, plus preview outside its two exact
 * ad-row spellings). Matched as substrings of the COMPACT key.
 */
const BANNED_KEY_FAMILIES = ["cost", "spend", "conversion", "preview", "keyword", "targeting"];

export function isSpendShapedKey(key: string): boolean {
  // Family scan runs on the COMPACT form of the whole key (lowercased, every
  // separator stripped). Tokenizing first can cut a family word in half
  // (keyWords -> key + words, tarGeting -> tar + geting, conVersions ->
  // con + versions) and dodge any per-token check, so the compact substring
  // scan is the load-bearing ban; it strictly subsumes prefix matches like
  // costPerLead and embedded spellings like retargeting/adkeywords.
  const compact = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (BANNED_KEY_FAMILIES.some((family) => compact.includes(family))) return true;

  // Exact-token pass for the short spellings (cpc/cpm/roi/budget/micros...)
  // where substring matching would be far too aggressive.
  const tokens = key
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  return tokens.some((token) => SPEND_SHAPED_KEYS.includes(token));
}
